import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { api, formatErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const empty = { name: "", sort_order: 100, adults_only: false };

export default function CategoriesPanel() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);

  const load = async () => { const { data } = await api.get("/admin/treatment-categories"); setItems(data || []); };
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      const payload = { name: form.name.trim(), sort_order: Number(form.sort_order) || 100, adults_only: !!form.adults_only };
      if (form.id) await api.put(`/admin/treatment-categories/${form.id}`, payload);
      else await api.post("/admin/treatment-categories", payload);
      toast.success("Saved"); setOpen(false); load();
    } catch (e) { toast.error(formatErr(e.response?.data?.detail) || "Could not save category"); }
  };

  const remove = async (c) => { if (!window.confirm(`Delete category "${c.name}"?`)) return; await api.delete(`/admin/treatment-categories/${c.id}`); load(); };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="font-display text-3xl">Treatment categories</h2>
          <p className="text-sm text-muted-foreground mt-1">Group treatments into categories shown on the guest menu.</p>
        </div>
        <Button onClick={() => { setForm(empty); setOpen(true); }} className="rounded-full bg-primary hover:bg-primary/90" data-testid="new-category-btn">
          <Plus className="h-4 w-4 mr-2" /> New category
        </Button>
      </div>

      <div className="bg-card border border-border overflow-hidden" data-testid="categories-list">
        {items.length === 0 && <div className="p-10 text-center text-muted-foreground text-sm">No categories yet.</div>}
        {items.map((c, i) => (
          <div key={c.id} className={`p-5 flex items-center justify-between ${i > 0 ? "border-t border-border" : ""}`} data-testid={`category-${c.id}`}>
            <div>
              <div className="font-display text-xl flex items-center gap-2">
                {c.name}
                {c.adults_only && (
                  <span className="text-[10px] uppercase tracking-widest bg-primary/10 text-primary px-2 py-0.5" data-testid={`category-adults-tag-${c.id}`}>18+</span>
                )}
              </div>
              <div className="overline text-primary/50 text-[10px]">Sort: {c.sort_order}</div>
            </div>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={() => { setForm({ ...c }); setOpen(true); }} data-testid={`edit-category-${c.id}`}><Pencil className="h-3 w-3" /></Button>
              <Button size="sm" variant="outline" onClick={() => remove(c)} className="text-destructive border-destructive/40" data-testid={`delete-category-${c.id}`}><Trash2 className="h-3 w-3" /></Button>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="category-dialog">
          <DialogHeader><DialogTitle className="font-display text-2xl">{form.id ? "Edit" : "New"} category</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label className="overline text-primary/60">Name</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Massage, Facial, Body ritual" data-testid="ctf-name" className="mt-2" /></div>
            <div><Label className="overline text-primary/60">Sort order (lower = first)</Label>
              <Input type="number" value={form.sort_order} onChange={(e) => setForm({ ...form, sort_order: e.target.value })} data-testid="ctf-sort" className="mt-2" /></div>
            <label className="flex items-start gap-3 border border-border bg-muted/30 p-3 cursor-pointer">
              <input type="checkbox" checked={!!form.adults_only} onChange={(e) => setForm({ ...form, adults_only: e.target.checked })} className="mt-1 h-4 w-4" data-testid="ctf-adults-only" />
              <span className="text-sm">
                <span className="font-medium">Adults only (18+)</span>
                <span className="block text-[11px] text-muted-foreground mt-0.5">
                  Every treatment in this category is reserved for adults. Under-18 guests can still browse them on the menu but the booking button is disabled with a polite note. Set on individual treatments too when the category as a whole is family-friendly.
                </span>
              </span>
            </label>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button onClick={save} className="bg-primary hover:bg-primary/90" data-testid="ctf-save">Save</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
